import { Alert } from '@warp-ds/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { startOfDay } from 'shared/util/datetime-fns';
import type { CalendarEvent } from '@/components/calendar/utils/dates';
import { CompactCalendar } from '@/components/compactCalendar/CompactCalendar';
import type { FhhState } from '@/domain';
import { EventTypes } from '@/domain';
import { useAvailabilityPerDate } from '@/hooks/useAvailabilityPerDate';
import { useObject } from '@/hooks/useObject';
import { usePreview } from '@/hooks/usePreview';
import { isDirectBookingForBrandEnabled } from '@/lib/helpers/importBrands';
import { AvailabilityForImport } from '../../components/availability/AvailabilityForImport';
import { ImportAdButton } from '../../components/importAdButton/ImportAdButton';
import { generateDayProps } from '../../utils';
import { ClassifiedCalendarContactForm } from './ClassifiedCalendarContactForm';
import { TjvrCalendarContactForm } from './TjvrCalendarContactForm';

export interface Props {
    isLoggedIn: boolean;
    selectedDates: { from: Date | null; to: Date | null };
    selectedNumGuests: number | null;
    norwegianAddress: boolean;
    today?: Date;
}

export const Calendar = ({
    isLoggedIn,
    selectedDates: initialSelectedDates,
    selectedNumGuests: initialSelectedNumGuests,
    norwegianAddress,
    today = new Date(),
}: Props) => {
    const objectData = useObject();
    const { adId, bookings, isImport, organisation, bookingEnabled, user } = objectData;
    const availabilityPerDate = useAvailabilityPerDate();
    const isPreview = usePreview();

    const initialFrom = initialSelectedDates?.from ? startOfDay(initialSelectedDates.from) : null;
    const initialTo = initialSelectedDates?.to ? startOfDay(initialSelectedDates.to) : null;

    const filters = useSelector((state: FhhState) => state.results.filters);
    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(
        (filters?.booking_from ? new Date(filters.booking_from) : initialFrom) ?? null,
    );
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(
        (filters?.booking_to ? new Date(filters.booking_to) : initialTo) ?? null,
    );

    const selectedDates = { from: selectedStartDate, to: selectedEndDate };
    const selectedNumGuests = filters?.no_of_beds_from ? Number(filters.no_of_beds_from) : (initialSelectedNumGuests ?? null);

    if (!adId || !bookingEnabled) {
        return null;
    }

    const calendarEvents: CalendarEvent[] =
        bookings?.map((booking) => ({
            id: String(booking.bookingId),
            type: EventTypes.BOOKING,
            startDate: new Date(booking.startDate),
            endDate: new Date(booking.endDate),
        })) ?? [];

    const handleSelectChange = ({ startDate, endDate }: { startDate: Date | null; endDate: Date | null }) => {
        if (isPreview) {
            return;
        }

        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
    };

    const handleDateReset = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
    };

    return (
        <section data-testid="adCalendar" aria-labelledby="objectCalendarHeading">
            <CompactCalendar
                availabilityPerDate={availabilityPerDate}
                calendarDayPropMiddleware={generateDayProps}
                events={calendarEvents}
                onReset={handleDateReset}
                onSelectChange={handleSelectChange}
                selectedDates={selectedDates}
                today={startOfDay(today)}
                title="Velg tid i kalenderen"
            />
            <Alert type="info" show={norwegianAddress && user?.isOwner}>
                Det er foreløpig ikke mulig for gjester å velge innsjekk nærmere enn en dag frem i tid
            </Alert>
            {isDirectBookingForBrandEnabled(String(organisation?.importId)) ? (
                <AvailabilityForImport
                    adId={adId}
                    noAvailableDates={availabilityPerDate?.length === 0}
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                />
            ) : isImport ? (
                <ImportAdButton objectData={objectData} checkInDate={selectedStartDate} checkOutDate={selectedEndDate} />
            ) : norwegianAddress ? (
                <TjvrCalendarContactForm endDate={selectedEndDate} startDate={selectedStartDate} numGuests={selectedNumGuests} />
            ) : (
                <ClassifiedCalendarContactForm
                    closeForm={handleDateReset}
                    endDate={selectedEndDate}
                    isLoggedIn={isLoggedIn}
                    startDate={selectedStartDate}
                />
            )}
        </section>
    );
};
